import React, { useState, useEffect, useMemo } from 'react';
import _ from 'lodash';
import {
  ResumeScreenTestCandidate,
  ResumeScreeningConfig,
  ResumeScreeningStatus,
  UpdateJobConfigTestSetFields,
  ResumeScreeningTestResult,
} from '@scale/shared-types';
import {
  Icon,
  Button,
  Typography,
  SelectMenu,
  Modal,
  Spinner,
  Table,
  Progress,
  TextArea,
} from '@scale/scaleui';
import { useQuery, useMutation } from 'react-query';
import {
  getResumeTestingSetUrls,
  triggerResumeScreeningTest,
  updateJobConfigTestSet,
} from 'react-scale/corp/lib/api/growth';
import {
  RESULT_COLUMNS,
  ResumeScreeningTestSteps,
} from 'react-scale/components/GrowthCenter/constants';
import resumeScreenGraphic from '../icons/resume-screen-icons.svg';
import moment from 'moment';
import { CSVLink } from 'react-csv';
import { imageImportSrc } from 'lib/imageImportSrc';

const NUM_STEPS = Object.keys(ResumeScreeningTestSteps).filter(key => !isNaN(Number(key))).length; // Have to filter since the reverse mappings are also included

export const GrowthCenterResumeScreeningTestModal: React.FC<{
  open: boolean;
  onClose: () => void;
  jobConfig: ResumeScreeningConfig;
  updateAllJobConfigs: (jobConfig: ResumeScreeningConfig) => void;
}> = ({ open, onClose, jobConfig, updateAllJobConfigs }) => {
  const [activeModalStep, setActiveModalStep] = useState<ResumeScreeningTestSteps>(
    ResumeScreeningTestSteps.Intro,
  );
  const [emailExpectedResultPairs, setEmailExpectedResultPairs] = useState<Record<string, string>>(
    {},
  );
  const [emails, setEmails] = useState<string>(
    emailExpectedResultPairs ? Object.keys(emailExpectedResultPairs).join(', ') : '',
  );
  const [resumesFetched, setResumesFetched] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    setActiveModalStep(ResumeScreeningTestSteps.Intro);
  }, [open]);

  useEffect(() => {
    if (jobConfig?.resumeScreeningTestSet) {
      const newPairs = jobConfig.resumeScreeningTestSet.reduce((acc, candidate) => {
        acc[candidate.email] = candidate.expectedResult;
        return acc;
      }, {} as Record<string, string>);
      setEmailExpectedResultPairs(newPairs);
    }
  }, [jobConfig?.resumeScreeningTestSet]);

  useEffect(() => {
    setEmails(Object.keys(emailExpectedResultPairs).join(', '));
  }, [emailExpectedResultPairs]);

  const onEmailInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEmails(e.target.value);
  };

  const { mutate: updateJobConfigTestSetMutate, isLoading: isTestSetUpdateLoading } = useMutation(
    (fields: UpdateJobConfigTestSetFields) => {
      return updateJobConfigTestSet(fields);
    },
  );

  const { data: emailResumePairs, isFetching: isFetchingResumes } = useQuery(
    ['fetchResumes'],
    () =>
      getResumeTestingSetUrls(
        emails
          .trim()
          .replace(/^,|,$/g, '')
          .split(/[\s,]+/)
          .map(email => email.trim())
          .filter(email => email !== ''),
        jobConfig._id,
      ),
    {
      enabled: activeModalStep === ResumeScreeningTestSteps.InputExpectedResults && !resumesFetched, // Only fetch when in the InputExpectedResults step
      onSuccess: () => setResumesFetched(true),
      onError: (error: any) => {
        setError(error.response.message);
      },
    },
  );

  const {
    mutate: triggerResumeScreeningTestMutate,
    isLoading: isResumeScreenTestLoading,
    data: resumeScreenTestData,
  } = useMutation(
    async () => {
      const { results, passedTest } = await triggerResumeScreeningTest(
        jobConfig._id,
        jobConfig.questionsToAskGPT,
        jobConfig.assistantDescription,
        jobConfig.resumeScreeningTestSet,
        jobConfig.requiresAllQuestionsPassed,
      );

      jobConfig.tested = passedTest;
      updateAllJobConfigs(jobConfig);

      return results;
    },
    {
      onError: (error: any) => {
        setError(error.response.message);
      },
    },
  );

  const updateResumeScreeningTestSetAndTriggerTest = async () => {
    if (!emailResumePairs) return;

    const emailResultPairs = Object.entries(emailExpectedResultPairs);
    const resumeScreeningTestSet: ResumeScreenTestCandidate[] = emailResultPairs.map(pair => ({
      email: pair[0].trim(),
      expectedResult: pair[1] === 'PASS' ? ResumeScreeningStatus.PASS : ResumeScreeningStatus.FAIL,
      resumeUrl: emailResumePairs[pair[0]].cdsUrl,
    }));

    // For draft job configs, skip the update and trigger the test
    if (!jobConfig._id) {
      jobConfig.resumeScreeningTestSet = resumeScreeningTestSet;
      updateAllJobConfigs(jobConfig);
      triggerResumeScreeningTestMutate();
      return;
    }

    jobConfig.resumeScreeningTestSet = resumeScreeningTestSet; // update local state so test uses the new test set
    updateAllJobConfigs(jobConfig);
    updateJobConfigTestSetMutate(
      {
        jobId: jobConfig._id,
        resumeScreeningTestSet,
      },
      {
        onSuccess: () => triggerResumeScreeningTestMutate(),
        onError: (error: any) => {
          setError(error.response.message);
        },
      },
    );
  };

  const onGoBack = () => {
    if (activeModalStep === ResumeScreeningTestSteps.Intro) {
      onCloseModal();
      return;
    }
    setActiveModalStep(activeModalStep - 1);
    setResumesFetched(false);
  };

  const onContinue = () => {
    if (activeModalStep === ResumeScreeningTestSteps.InputEmails) {
      const splitEmails = emails
        .trim()
        .replace(/^,|,$/g, '')
        .split(/[\s,]+/)
        .map(email => email.trim())
        .filter(email => email !== '');
      const updatedEmailExpectedResultPairs = splitEmails.reduce((acc, email) => {
        acc[email] = emailExpectedResultPairs[email] ?? 'SELECT';
        return acc;
      }, {} as Record<string, string>);
      setEmailExpectedResultPairs(updatedEmailExpectedResultPairs);
    } else if (activeModalStep === ResumeScreeningTestSteps.InputExpectedResults) {
      updateResumeScreeningTestSetAndTriggerTest();
    } else if (activeModalStep === ResumeScreeningTestSteps.TestResults) {
      onCloseModal();
    }
    setActiveModalStep(activeModalStep + 1);
  };

  const isContinueDisabled = () => {
    if (activeModalStep === ResumeScreeningTestSteps.InputEmails) {
      return emails.length === 0;
    }
    if (activeModalStep === ResumeScreeningTestSteps.InputExpectedResults) {
      return Object.values(emailExpectedResultPairs).some(value => value === 'SELECT');
    }
    return false;
  };

  const onCloseModal = () => {
    onClose();
    setActiveModalStep(ResumeScreeningTestSteps.Intro);
    setResumesFetched(false);
    setError('');
  };

  return (
    <Modal
      innerClassName="scaleui scale-tailwind scale-theme w-[50%] h-[50%]"
      title={<></>}
      open={open}
      onClose={onCloseModal}
      actions={
        <div className="flex items-center gap-2 justify-end">
          {activeModalStep !== ResumeScreeningTestSteps.Intro && (
            <Button onClick={onGoBack} variant="neutral">
              Go back
            </Button>
          )}
          <Button onClick={onContinue} variant="primary" disabled={isContinueDisabled()}>
            {activeModalStep === ResumeScreeningTestSteps.TestResults ? 'Close' : 'Continue'}
          </Button>
        </div>
      }
    >
      {activeModalStep === ResumeScreeningTestSteps.Intro && <IntroResumeScreeningTestContent />}
      {activeModalStep === ResumeScreeningTestSteps.InputEmails && (
        <InputCandidateEmailsContent emails={emails} onEmailInputChange={onEmailInputChange} />
      )}
      {activeModalStep === ResumeScreeningTestSteps.InputExpectedResults && (
        <PreviewResumesAndResultContent
          emailResumePairs={emailResumePairs}
          emailExpectedResultPairs={emailExpectedResultPairs}
          setEmailExpectedResultPairs={setEmailExpectedResultPairs}
          isLoading={isFetchingResumes}
          error={error}
        />
      )}
      {activeModalStep === ResumeScreeningTestSteps.TestResults && (
        <ResumeScreeningTestResults
          isLoading={isTestSetUpdateLoading || isResumeScreenTestLoading}
          resumeScreenTestData={resumeScreenTestData}
          error={error}
        />
      )}
    </Modal>
  );
};

const IntroResumeScreeningTestContent: React.FC = () => {
  const progress = ((ResumeScreeningTestSteps.Intro + 1) / NUM_STEPS) * 100;

  return (
    <div className="flex flex-col gap-4 h-full">
      <div className="flex flex-row bg-neutral-100 h-[40%] items-center justify-center rounded-md">
        <img src={imageImportSrc(resumeScreenGraphic)} alt="Resume Screening" />
      </div>
      <Progress percentComplete={progress} />
      <Typography.Subtitle2>Test Your AI Assistant</Typography.Subtitle2>
      <Typography.Paragraph2>
        To test your AI assistant, please add contributors' emails on the following screen and
        revise your questions or description if the answers are not as expected.
      </Typography.Paragraph2>
    </div>
  );
};

const InputCandidateEmailsContent: React.FC<{
  emails: string;
  onEmailInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}> = ({ emails, onEmailInputChange }) => {
  const progress = ((ResumeScreeningTestSteps.InputEmails + 1) / NUM_STEPS) * 100;
  return (
    <div className="flex flex-col gap-4 h-full">
      <Progress percentComplete={progress} />
      <div>
        <Typography.Subtitle2>Input Candidate Emails for Test Resumes</Typography.Subtitle2>
        <Typography.Paragraph2>
          Enter a list of emails and we will pull in their resumes from Greenhouse or their Worker
          Profile.
        </Typography.Paragraph2>
      </div>
      <TextArea
        className=""
        label=""
        rows={8}
        value={emails}
        onChange={onEmailInputChange}
        placeholder="Enter emails separated by commas or newlines"
        resizable={false}
      />
    </div>
  );
};

const PreviewResumesAndResultContent: React.FC<{
  emailResumePairs?: Record<
    string,
    {
      signedUrl: string;
      cdsUrl: string;
    }
  >;
  emailExpectedResultPairs: Record<string, string>;
  setEmailExpectedResultPairs: (pairs: Record<string, string>) => void;
  isLoading: boolean;
  error?: string;
}> = ({
  emailResumePairs,
  emailExpectedResultPairs,
  setEmailExpectedResultPairs,
  isLoading,
  error,
}) => {
  const progress = ((ResumeScreeningTestSteps.InputExpectedResults + 1) / NUM_STEPS) * 100;

  const removeFromTestSet = (email: string) => {
    if (!emailResumePairs) return;

    // Remove the email from emailResumePairs
    delete emailResumePairs[email];

    // Remove the entry from emailExpectedResultPairs
    const updatedPairs = { ...emailExpectedResultPairs };
    delete updatedPairs[email];
    setEmailExpectedResultPairs(updatedPairs);
  };

  return (
    <div className="flex flex-col gap-4 h-full">
      <Progress className="flex-shrink-0" percentComplete={progress} />
      <div>
        <Typography.Subtitle2>Preview Resumes and Select Result</Typography.Subtitle2>
        <Typography.Paragraph2>
          Indicate which resumes to reject or advance. We'll compare your answers against the AI
          assistant's responses to test for its accuracy.
        </Typography.Paragraph2>
        {error && (
          <div className="text-danger-900 font-medium inline-block rounded my-1">
            Error: {error}
          </div>
        )}
      </div>
      <div className="scaleui scale-tailwind scale-theme flex flex-col h-full">
        {isLoading ? (
          <div className="flex-grow flex flex-col items-center justify-center h-full">
            <Spinner size="md" />
          </div>
        ) : (
          emailResumePairs &&
          Object.entries(emailResumePairs).map(([email, { signedUrl }], index) => {
            return (
              <div key={index} className="flex flex-row gap-4 p-2">
                <div className="flex grow flex-row bg-neutral-100 p-1 rounded-md items-center">
                  <Icon icon={['fas', 'file']} size="sm" />
                  <a href={signedUrl} target="_blank" rel="noopener noreferrer" className="ml-2">
                    <Typography.Subtitle3>{email}</Typography.Subtitle3>
                  </a>
                  <div className="flex-grow"></div>
                  <Icon
                    icon={['fas', 'times']}
                    size="sm"
                    onClick={() => removeFromTestSet(email)}
                  />
                </div>
                <div className="flex items-center">
                  <SelectMenu
                    className="min-w-[150px]"
                    options={[
                      { id: 'SELECT', name: 'Select answer' },
                      { id: 'PASS', name: 'Pass' },
                      { id: 'FAIL', name: 'Fail' },
                    ]}
                    selectedOptionId={emailExpectedResultPairs[email] ?? 'SELECT'}
                    setSelected={(id: string) => {
                      emailExpectedResultPairs[email] = id;
                      setEmailExpectedResultPairs({ ...emailExpectedResultPairs });
                    }}
                    menuDirection="bottom"
                  />
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const ResumeScreeningTestResults: React.FC<{
  isLoading: boolean;
  resumeScreenTestData: ResumeScreeningTestResult[];
  error?: string;
}> = ({ isLoading, resumeScreenTestData, error }) => {
  const progress = ((ResumeScreeningTestSteps.TestResults + 1) / NUM_STEPS) * 100;

  const falsePositives = resumeScreenTestData?.filter(
    (result: ResumeScreeningTestResult) =>
      result.expectedResult === ResumeScreeningStatus.FAIL &&
      result.actualResult === ResumeScreeningStatus.PASS,
  );
  const falseNegatives = resumeScreenTestData?.filter(
    (result: ResumeScreeningTestResult) =>
      result.expectedResult === ResumeScreeningStatus.PASS &&
      result.actualResult === ResumeScreeningStatus.FAIL,
  );
  const accurateResults = resumeScreenTestData?.filter(
    (result: ResumeScreeningTestResult) => result.expectedResult === result.actualResult,
  );
  const accuracyPercentage =
    _.round(accurateResults?.length / resumeScreenTestData?.length, 2) * 100;

  const getResultClass = (result: ResumeScreeningStatus, isAccurate: boolean) => {
    if (isAccurate) return '';

    return result === ResumeScreeningStatus.PASS
      ? 'bg-success-100 text-success-900 font-bold inline-block rounded px-1'
      : 'bg-danger-100 text-danger-900 font-bold inline-block rounded px-1';
  };

  const date = moment().format('YYYY-MM-DD');
  const resumeScreenTestCSVFilename = `resume-screening-test-results-${date}.csv`;
  const resumeScreenTestCSVHeaders = ['Email', 'Expected Result', 'Actual Result', 'LLM Responses'];
  const resumeScreenTestResultsCSVData = useMemo<string[][]>(() => {
    return resumeScreenTestData?.map((result: ResumeScreeningTestResult) => {
      return [
        result.email,
        result.expectedResult,
        result.actualResult.toString(),
        result.llmRawResponses.join(',\n').replace(/"/g, '""'),
      ];
    });
  }, [resumeScreenTestData]);

  const resumeScreenTestDataFormatted = resumeScreenTestData?.map((result: any) => {
    const isAccurate = result.expectedResult === result.actualResult;

    return {
      email: result.email,
      expectedResult: (
        <div className={getResultClass(result.expectedResult, isAccurate)}>
          {_.capitalize(_.toLower(result.expectedResult))}
        </div>
      ),
      actualResult: (
        <div className={getResultClass(result.actualResult, isAccurate)}>
          {_.capitalize(_.toLower(result.actualResult))}
        </div>
      ),
    };
  });

  return (
    <div className="flex flex-col gap-4 h-full">
      <Progress className="flex-shrink-0" percentComplete={progress} />
      <div className="flex flex-row justify-between items-start">
        <div>
          <Typography.Subtitle2>Resume Screening Test Results</Typography.Subtitle2>
          <Typography.Paragraph2>
            {!isLoading &&
              !_.isEmpty(resumeScreenTestData) &&
              `Overall accuracy: ${accuracyPercentage}% (${accurateResults?.length}/${resumeScreenTestData?.length}). False positives: ${falsePositives?.length}. False negatives: ${falseNegatives?.length}.`}
          </Typography.Paragraph2>
          {error && (
            <div className="text-danger-900 font-medium inline-block rounded my-1">
              Error: {error}
            </div>
          )}
        </div>
        {!isLoading && resumeScreenTestResultsCSVData && (
          <Button fullWidth={false} variant="neutral" size="sm">
            <CSVLink
              data={resumeScreenTestResultsCSVData}
              headers={resumeScreenTestCSVHeaders}
              filename={resumeScreenTestCSVFilename}
            >
              Export as CSV
            </CSVLink>
          </Button>
        )}
      </div>
      {isLoading && !error ? (
        <div className="flex-grow flex flex-col items-center justify-center">
          <Spinner size="md" />
        </div>
      ) : (
        resumeScreenTestDataFormatted && (
          <Table
            columns={RESULT_COLUMNS}
            data={resumeScreenTestDataFormatted}
            defaultPageSize={resumeScreenTestData.length < 10 ? resumeScreenTestData.length : 10}
          />
        )
      )}
    </div>
  );
};
